import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageSquare, Check } from "lucide-react";
import { BRAND } from "@/data/content";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitContact(form);
            setDone(true);
            toast.success("Message sent. We reply within 48 hours.");
            setForm({ name: "", email: "", subject: "", message: "", website: "" });
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) toast.error("Too many messages — please wait a minute and try again.");
            else toast.error("Couldn't send the message. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Contact"
                title="Send a note. We read every one."
                subtitle="Questions, feedback, collab ideas — the form goes straight to our inbox."
            />
            <section className="container-editorial pb-24 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-4 space-y-6">
                    <div className="flex items-start gap-3">
                        <Mail className="h-5 w-5 text-primary mt-1" />
                        <div>
                            <p className="font-serif text-lg">Email</p>
                            <a href={`mailto:${BRAND.email}`} data-testid="contact-email-link" className="text-stone-600 hover:text-primary text-sm">{BRAND.email}</a>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <MessageSquare className="h-5 w-5 text-primary mt-1" />
                        <div>
                            <p className="font-serif text-lg">Response time</p>
                            <p className="text-stone-600 text-sm">Under 48 hours, Mon–Fri.</p>
                        </div>
                    </div>
                </div>
                <form onSubmit={submit} data-testid="contact-form" className="md:col-span-8 space-y-4 bg-white border border-stone-200 rounded-lg p-6 md:p-10">
                    {done ? (
                        <div className="text-center py-8" data-testid="contact-success">
                            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 grid place-items-center">
                                <Check className="h-6 w-6 text-primary" />
                            </div>
                            <p className="font-serif text-2xl mt-4">Thanks — we've got it.</p>
                            <p className="text-stone-600 mt-2">We'll be in touch within 48 hours.</p>
                            <Button onClick={() => setDone(false)} variant="outline" className="mt-6 rounded-full border-stone-300">Send another</Button>
                        </div>
                    ) : (
                        <>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-stone-500">Name</label>
                                    <Input data-testid="contact-name" required value={form.name} onChange={upd("name")} className="mt-1 h-11" placeholder="Alex Carter" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-stone-500">Email</label>
                                    <Input data-testid="contact-email" type="email" required value={form.email} onChange={upd("email")} className="mt-1 h-11" placeholder="you@example.com" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest text-stone-500">Subject</label>
                                <Input data-testid="contact-subject" value={form.subject} onChange={upd("subject")} className="mt-1 h-11" placeholder="What's this about?" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest text-stone-500">Message</label>
                                <Textarea data-testid="contact-message" required value={form.message} onChange={upd("message")} className="mt-1 min-h-32" placeholder="Tell us everything…" />
                            </div>
                            {/* Honeypot — hidden from humans, catches bots */}
                            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
                                <label>Website (leave blank)</label>
                                <input
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    name="website"
                                    value={form.website}
                                    onChange={upd("website")}
                                />
                            </div>
                            <Button data-testid="contact-submit" disabled={loading} className="rounded-full bg-primary hover:bg-primary/90 h-12 px-7 text-primary-foreground">
                                {loading ? "Sending…" : "Send message"}
                            </Button>
                        </>
                    )}
                </form>
            </section>
        </>
    );
}
